import { Component } from '@theme/component';
import { scrollTo } from '@theme/scroll-container';

/**
 * @typedef {Object} BackToTopRefs
 * @property {HTMLButtonElement} button - The scroll-to-top trigger
 */

/**
 * Shows a floating back-to-top button after the main buy buttons scroll out of view.
 *
 * @extends {Component<BackToTopRefs>}
 */
class BackToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #isVisible = false;

  connectedCallback() {
    super.connectedCallback();

    const { button } = this.refs;
    button.addEventListener('click', this.scrollToTop, { signal: this.#abortController.signal });

    this.#setupIntersectionObserver();

    customElements.whenDefined('shopify-chat').then(() => {
      if (this.#abortController.signal.aborted) return;
      if (this.#isVisible && this.#isChatActive()) this.#hideButton();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
  }

  scrollToTop = (event) => {
    event?.preventDefault();
    scrollTo({ top: 0, behavior: 'smooth' });
  };

  #setupIntersectionObserver() {
    const buyButtonsBlock = document.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showButton();
          return;
        }
      }

      this.#hideButton();
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
  }

  #showButton() {
    if (this.#isVisible) return;

    const { button } = this.refs;
    this.#isVisible = true;
    button.classList.add('is-visible');
    button.setAttribute('aria-hidden', 'false');
  }

  #hideButton() {
    if (!this.#isVisible) return;

    const { button } = this.refs;
    this.#isVisible = false;
    button.classList.remove('is-visible');
    button.setAttribute('aria-hidden', 'true');
  }

  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }
}

if (!customElements.get('back-to-top')) {
  customElements.define('back-to-top', BackToTopComponent);
}
